import * as THREE from 'three';
import type { CharacterStyle } from './characterStyle';
import { GeoBuilder, lathe, Tone, type Detail, type Ring, type Sink } from './figure';
import { Joint, type Proportions } from './skeleton';

/** Player-only tailoring and sculpt. All coordinates are joint-local; all colour is baked by
 * GeoBuilder. The shared crowd pieces deliberately do not depend on this module. */
type Point = readonly [number, number];
type Vertex = readonly [number, number, number];
type Surface = ((x: number, y: number) => number) & { facets: Vertex[][] };
const box = (x: number, y: number, z: number) => new THREE.BoxGeometry(x, y, z);
const oval = (x: number, y: number, z: number, detail: Detail = 'high') =>
  new THREE.SphereGeometry(1, detail === 'high' ? 8 : 6, detail === 'high' ? 5 : 4).scale(x, y, z);

/** A thin, tessellated cloth/face panel following the underlying sculpt, not a floating box.
 * Triangulation handles the concave lapel notches. Subdivision follows the curved chest. */
function panel(b: GeoBuilder, points: readonly Point[], surface: (x: number, y: number) => number, tone: number, shade = 1, steps = 2, backing?: Surface): void {
  const vertices: number[] = [];
  const indices: number[] = [];
  const shared = new Map<string, number>();
  const faces = THREE.ShapeUtils.triangulateShape(points.map(([x, y]) => new THREE.Vector2(x, y)), []);
  const emit = (a: Point, c: Point, d: Point, n: number): void => {
    if (n > 0) {
      const ac: Point = [(a[0] + c[0]) / 2, (a[1] + c[1]) / 2];
      const cd: Point = [(c[0] + d[0]) / 2, (c[1] + d[1]) / 2];
      const da: Point = [(d[0] + a[0]) / 2, (d[1] + a[1]) / 2];
      emit(a, ac, da, n - 1); emit(ac, c, cd, n - 1); emit(da, cd, d, n - 1); emit(ac, cd, da, n - 1);
    } else {
      const cross = (c[0] - a[0]) * (d[1] - a[1]) - (c[1] - a[1]) * (d[0] - a[0]);
      if (Math.abs(cross) < 1e-12) return;
      for (const p of cross > 0 ? [a, c, d] : [a, d, c]) {
        const key = `${p[0].toFixed(8)},${p[1].toFixed(8)}`;
        let index = shared.get(key);
        if (index === undefined) {
          index = vertices.length / 3;
          shared.set(key, index);
          vertices.push(p[0], p[1], surface(...p));
        }
        indices.push(index);
      }
    }
  };
  for (const f of faces) {
    const tri = [points[f[0]!]!, points[f[1]!]!, points[f[2]!]!];
    if (!backing) { emit(tri[0]!, tri[1]!, tri[2]!, steps); continue; }
    // Clip the panel against the actual underlying triangles. Every resulting face is coplanar
    // with its backing, even at the collar's sharp folds and the side of the jaw.
    for (const facet of backing.facets) {
      let poly = tri;
      for (let edge = 0; edge < 3 && poly.length; edge++) {
        const a = facet[edge]!; const c = facet[(edge + 1) % 3]!;
        const distance = (p: Point): number => (c[0] - a[0]) * (p[1] - a[1]) - (c[1] - a[1]) * (p[0] - a[0]);
        const clipped: Point[] = [];
        for (let i = 0; i < poly.length; i++) {
          const p = poly[i]!; const q = poly[(i + 1) % poly.length]!;
          const dp = distance(p); const dq = distance(q);
          if (dp >= -1e-12) clipped.push(p);
          if ((dp > 0 && dq < 0) || (dp < 0 && dq > 0)) {
            const t = dp / (dp - dq);
            clipped.push([p[0] + (q[0] - p[0]) * t, p[1] + (q[1] - p[1]) * t]);
          }
        }
        poly = clipped;
      }
      for (let i = 1; i < poly.length - 1; i++) emit(poly[0]!, poly[i]!, poly[i + 1]!, 0);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
  g.setIndex(indices);
  b.add(g, tone, shade);
}

/** Exact polygonal loft surface, including the angular facets between ring vertices. */
function surface(rings: readonly Ring[], segments: number): Surface {
  const facets: Vertex[][] = [];
  const vertex = (i: number, k: number): Vertex => {
    const ring = rings[i]!; const a = k / segments * Math.PI * 2;
    return [Math.sin(a) * ring.r, ring.y, Math.cos(a) * (ring.rz ?? ring.r) + (ring.dz ?? 0)];
  };
  for (let i = 0; i < rings.length - 1; i++) for (let k = 0; k < segments; k++) {
    if (Math.cos((k + 0.5) / segments * Math.PI * 2) < 0) continue;
    const a = vertex(i, k); const b = vertex(i, k + 1); const c = vertex(i + 1, k); const d = vertex(i + 1, k + 1);
    facets.push([a, b, c], [b, d, c]);
  }
  const zAt = (x: number, y: number): number => {
    for (const [a, b, c] of facets) {
      const det = (b![1] - c![1]) * (a![0] - c![0]) + (c![0] - b![0]) * (a![1] - c![1]);
      if (Math.abs(det) < 1e-12) continue;
      const u = ((b![1] - c![1]) * (x - c![0]) + (c![0] - b![0]) * (y - c![1])) / det;
      const v = ((c![1] - a![1]) * (x - c![0]) + (a![0] - c![0]) * (y - c![1])) / det;
      if (u >= -1e-7 && v >= -1e-7 && u + v <= 1.0000001) return u * a![2] + v * b![2] + (1 - u - v) * c![2];
    }
    let i = 0;
    while (i < rings.length - 2 && y > rings[i + 1]!.y) i++;
    const a = rings[i]!; const c = rings[i + 1]!;
    const t = THREE.MathUtils.clamp((y - a.y) / (c.y - a.y), 0, 1);
    const r = THREE.MathUtils.lerp(a.r, c.r, t);
    const rz = THREE.MathUtils.lerp(a.rz ?? a.r, c.rz ?? c.r, t);
    const xx = Math.min(Math.abs(x), r * 0.999);
    const angle = Math.asin(xx / r);
    const step = Math.PI * 2 / segments;
    const lo = Math.floor(angle / step) * step;
    const hi = lo + step;
    const u = (xx / r - Math.sin(lo)) / (Math.sin(hi) - Math.sin(lo));
    return rz * THREE.MathUtils.lerp(Math.cos(lo), Math.cos(hi), u) + THREE.MathUtils.lerp(a.dz ?? 0, c.dz ?? 0, t);
  };
  return Object.assign(zAt, { facets });
}

export function villainTorso(sink: Sink, d: Proportions, detail: Detail): void {
  const h = d.height; const w = d.width; const dep = d.depth;
  const n = detail === 'high' ? 20 : 12;
  const waist = d.waistRadius;
  const c = sink(Joint.chest); const sp = sink(Joint.spine);
  // Pass 4 landmarks (see villainProportions): a seat that no longer flares past the hands, a
  // real waist, a ribcage narrower than the shoulders (the arms hang against it, not beside it),
  // and a trapezius that slopes ~19° from the collar to the deltoid instead of a square yoke.
  const wr: Ring[] = [
    { y: -0.073 * h, r: waist * 1.15, rz: waist * 1.0 * dep },
    { y: -0.01 * h, r: waist * 1.09, rz: waist * 0.91 * dep },
    { y: 0.035 * h, r: waist * 1.04, rz: waist * 0.8 * dep },
    { y: d.chest - 0.025 * h, r: waist * 1.0, rz: waist * 0.75 * dep },
  ];
  const rings: Ring[] = [
    ...wr.slice(0, -1).map(r => ({ ...r, y: r.y - d.chest })),
    { y: -0.025 * h, r: waist * 1.0, rz: waist * 0.75 * dep },
    { y: 0.04 * h, r: waist * 1.02, rz: 0.142 * dep, dz: 0.005 },
    { y: 0.105 * h, r: 0.182 * w, rz: 0.155 * dep, dz: 0.012 },
    { y: 0.18 * h, r: 0.192 * w, rz: 0.16 * dep, dz: 0.014 },
    { y: 0.24 * h, r: 0.195 * w, rz: 0.15 * dep, dz: 0.012 },
    { y: 0.268 * h, r: 0.193 * w, rz: 0.132 * dep, dz: 0.008 },
    { y: 0.29 * h, r: 0.178 * w, rz: 0.115 * dep, dz: 0.005 },
    { y: 0.313 * h, r: 0.142 * w, rz: 0.099 * dep },
    { y: 0.335 * h, r: 0.108 * w, rz: 0.086 * dep },
    { y: 0.35 * h, r: 0.081 * w, rz: 0.077 * dep },
    { y: 0.38 * h, r: 0.073 * w, rz: 0.066 * dep },
  ];
  const chest = lathe(rings, n, { top: true });
  const cp = chest.getAttribute('position');
  for (let i = 0; i < cp.count; i++) {
    const x = cp.getX(i); const y = cp.getY(i); const z = cp.getZ(i);
    if (z < 0) cp.setZ(i, z - 0.012 * Math.exp(-Math.pow((Math.abs(x) - 0.105 * w) / 0.058, 2) - Math.pow((y - 0.21 * h) / 0.075, 2)));
  }
  chest.computeVertexNormals();
  c.add(chest, Tone.A, 1, (x, y, z) =>
    (y < 0.05 ? 0.92 : 1) * (z < 0 ? 0.94 : 1) * (Math.abs(x) > 0.19 * w ? 0.97 : 1));
  const z = surface(rings, n);
  // Collar and lapel outlines were drawn for the old, taller yoke: compress them above the chest.
  const yoke = (y: number): number => y <= 0.2 ? y : 0.2 + (y - 0.2) * 0.93;
  const cloth = (points: readonly Point[], tone: number, shade: number, lift: number) =>
    panel(c, points.map(([x, y]) => [x * w, yoke(y) * h]), (x, y) => z(x, y) + lift, tone, shade, 0, z);
  // Shirt opening widens at the throat and narrows beneath the overlapping coat fronts.
  cloth([[-0.065, 0.392], [0.065, 0.392], [0.056, 0.285], [0.014, 0.105], [-0.025, 0.105], [-0.062, 0.285]], Tone.B, 0.91, 0.003);
  cloth([[-0.013, 0.344], [0.013, 0.344], [0.022, 0.177], [0.005, 0.148], [-0.018, 0.174]], Tone.C, 0.92, 0.007);
  cloth([[-0.019, 0.359], [0.02, 0.359], [0.012, 0.331], [-0.009, 0.331]], Tone.C, 1.13, 0.009);
  for (const s of [-1, 1]) {
    const mirror = (p: readonly Point[]): Point[] => p.map(([x, y]) => [x * s, y]);
    const shirtCollar: Point[] = [[0.009, 0.371], [0.053, 0.392], [0.066, 0.361], [0.037, 0.316]];
    cloth(mirror(shirtCollar), Tone.B, 0.61, 0.009);
    cloth(mirror(shirtCollar.map(([x, y]) => [x - 0.002, y + 0.004])), Tone.B, 1.04, 0.012);
    // Notched lapel with a dark turned edge and a broad folded plane.
    const lapel: Point[] = [[0.067, 0.375], [0.125, 0.328], [0.092, 0.308], [0.151, 0.292], [0.086, 0.18], [0.019, 0.083], [0.05, 0.278]];
    cloth(mirror(lapel), Tone.A, 0.64, 0.008);
    cloth(mirror(lapel.map(([x, y]) => [x - 0.002, y + 0.002])), Tone.A, s === 1 ? 1.1 : 1.04, 0.012);
    // A separate collar leaf folds down into the lapel notch.
    cloth(mirror([[0.068, 0.398], [0.13, 0.344], [0.112, 0.316], [0.068, 0.344]]), Tone.A, 0.94, 0.017);
  }
  // The chest and waist are one continuous surface; skin weights, rather than an overlapping
  // second cylinder, let the waist follow the spine through turns and weapon poses.
  const wz = surface(wr, n);
  // The overlap and buttons continue onto the waist, each on its own moving joint.
  panel(sp, [[-0.006, -0.06], [0.006, -0.06], [0.006, 0.13], [-0.006, 0.13]], (x, y) => wz(x, y) + 0.003, Tone.A, 0.73, 0, wz);
  for (const [b, yy, zz] of [[c, 0.09 * h, z(-0.025, 0.09 * h)], [sp, 0.075 * h, wz(-0.025, 0.075 * h)], [sp, -0.025 * h, wz(-0.025, -0.025 * h)]] as const) {
    b.add(new THREE.CylinderGeometry(0.0105, 0.011, 0.006, 8).rotateX(Math.PI / 2).translate(-0.025, yy, zz + 0.006), Tone.C, 0.78);
    if (detail === 'high') b.add(oval(0.002, 0.003, 0.001).translate(-0.027, yy, zz + 0.01), Tone.A, 0.75);
  }
  // Pocket welts follow the hip curvature. No projecting boxes at the side of the torso.
  for (const s of [-1, 1]) {
    const pts: Point[] = [[s * 0.075 * w, 0.025], [s * 0.166 * w, 0.055], [s * 0.165 * w, 0.03], [s * 0.075 * w, 0]];
    panel(sp, pts, (x, y) => wz(x, y) + 0.004, Tone.A, 0.64, 0, wz);
    panel(sp, pts.map(([x, y]) => [x, y + 0.008]), (x, y) => wz(x, y) + 0.009, Tone.A, 1.06, 0, wz);
  }
  // Back collar and half-belt: the former meets the nape; the latter sits at the natural waist.
  const back = lathe([{ y: 0.318 * h, r: 0.11 * w, rz: 0.093 * dep }, { y: 0.396 * h, r: 0.074 * w, rz: 0.068 * dep }], n);
  // Remove the front third: the collar wraps the nape, with an opening for the shirt and tie.
  const bi = back.getIndex()!; const bp = back.getAttribute('position'); const keep: number[] = [];
  for (let i = 0; i < bi.count; i += 3) {
    const ids = [bi.getX(i), bi.getX(i + 1), bi.getX(i + 2)];
    if (ids.reduce((sum, id) => sum + bp.getZ(id), 0) / 3 < 0.025) keep.push(...ids);
  }
  back.setIndex(keep); c.add(back, Tone.A, 0.94);
  const belt = new GeoBuilder();
  panel(belt, [[-0.143 * w, 0], [0.143 * w, 0], [0.143 * w, 0.034 * h], [-0.143 * w, 0.034 * h]], (x, y) => wz(x, y) + 0.005, Tone.A, 1, 0, wz);
  sp.add(belt.toGeometry().rotateY(Math.PI), Tone.A, 0.86);
  if (detail === 'high') {
    for (const s of [-1, 1]) sp.add(oval(0.009, 0.009, 0.004).translate(s * 0.112 * w, 0.017, -wz(s * 0.112 * w, 0.017) - 0.009), Tone.C, 0.8);
    cloth([[0.09, 0.228], [0.178, 0.24], [0.178, 0.227], [0.09, 0.215]], Tone.A, 0.7, 0.006);
  }
}

/** One uninterrupted sleeve from the rounded deltoid to the wrist. The elbow is weighted to
 * both existing arm joints, so it bends as cloth rather than exposing two capped cylinders. */
export function villainArm(b: GeoBuilder, d: Proportions, detail: Detail): void {
  const u = d.upperArm; const l = d.lowerArm; const k = d.limb;
  // The cap is lower and flatter than before: with the higher shoulder joint it now continues the
  // trapezius slope instead of sitting on it as a ball. The upper arm tapers deltoid → biceps →
  // elbow; two rings either side of the elbow keep its volume through a bend.
  const rings: Ring[] = [
    { y: 0.017, r: 0.006 * k, rz: 0.01 * k, dz: 0.006 },
    { y: 0.011, r: 0.037 * k, rz: 0.042 * k, dz: 0.007 },
    { y: -0.006, r: 0.057 * k, rz: 0.06 * k, dz: 0.007 },
    { y: -0.03, r: 0.065 * k, rz: 0.066 * k, dz: 0.006 },
    { y: -0.075, r: 0.064 * k, rz: 0.065 * k, dz: 0.005 },
    { y: -u * 0.45, r: 0.057 * k, rz: 0.063 * k, dz: 0.002 },
    { y: -u * 0.75, r: 0.05 * k, rz: 0.055 * k, dz: -0.003 },
    { y: -u + 0.03, r: 0.05 * k, rz: 0.054 * k, dz: -0.003 },
    { y: -u, r: 0.051 * k, rz: 0.056 * k, dz: -0.002 },
    { y: -u - 0.03, r: 0.054 * k, rz: 0.057 * k, dz: 0.002 },
    { y: -u - l * 0.3, r: 0.054 * k, rz: 0.055 * k, dz: 0.004 },
    { y: -u - l * 0.65, r: 0.046 * k, rz: 0.048 * k, dz: 0.003 },
    { y: -u - l + 0.025, r: 0.042 * k, rz: 0.044 * k },
    { y: -u - l + 0.009, r: 0.042 * k, rz: 0.044 * k },
  ];
  b.add(lathe(rings, detail === 'high' ? 12 : 8, { top: true }), Tone.A, 1, (_x, y, z) =>
    1 - 0.05 * Math.exp(-Math.pow((y + u) / 0.025, 2)) * Math.max(0, z / 0.06));
}

/** One trouser leg from hip to turn-up, built on the thigh joint and skinned across the knee
 * (villainWeights), so a bent knee folds cloth instead of opening a gap between two capped tubes.
 * Generous thigh, small kneecap, calf behind the shin and a restrained break over the shoe. */
export function villainLeg(b: GeoBuilder, d: Proportions, detail: Detail): void {
  const w = d.width; const u = d.upperLeg; const l = d.lowerLeg;
  const profile: Ring[] = [
    { y: 0.025, r: 0.091, rz: 0.088 },
    { y: -u * 0.17, r: 0.09, rz: 0.093, dz: 0.003 },
    { y: -u * 0.42, r: 0.081, rz: 0.085, dz: 0.007 },
    { y: -u * 0.7, r: 0.066, rz: 0.073, dz: 0.009 },
    { y: -u + 0.045, r: 0.059, rz: 0.064, dz: 0.006 },
    { y: -u, r: 0.057, rz: 0.062, dz: 0.005 },
    { y: -u - 0.045, r: 0.059, rz: 0.064, dz: -0.002 },
    { y: -u - l * 0.2, r: 0.06, rz: 0.066, dz: -0.008 },
    { y: -u - l * 0.38, r: 0.058, rz: 0.066, dz: -0.01 },
    { y: -u - l * 0.6, r: 0.049, rz: 0.054, dz: -0.005 },
    { y: -u - l * 0.8, r: 0.043, rz: 0.047 },
    { y: -u - l * 0.9, r: 0.049, rz: 0.051 },
    { y: -u - l, r: 0.05, rz: 0.053, dz: 0.004 },
  ];
  const rings = profile.map(r => ({ ...r, r: r.r * w, rz: r.rz! * w }));
  const n = detail === 'high' ? 12 : 8;
  b.add(lathe(rings, n, { top: true, bottom: true }), Tone.A, 1, (x, y, z) =>
    (y < -u - l * 0.9 ? 0.84 : 1) * (z > 0 && Math.abs(x) < 0.015 ? 1.13 : 1));
  if (detail === 'high') {
    const z = surface([...rings].reverse(), n);
    for (const s of [-1, 1]) panel(b, [[0, -0.04], [s * 0.003, -0.04], [s * 0.003, -u - l * 0.87], [0, -u - l * 0.87]],
      (x, y) => z(x, y) + 0.001, Tone.A, s === 1 ? 1.12 : 0.86, 0, z);
  }
}

/** Skin weights only; the skeleton, animation poses and attachment transforms stay unchanged.
 * Geometry stays in its usual joint-local bind space and can still be cached between players. */
export function villainWeights(g: THREE.BufferGeometry, joint: number, d: Proportions, tailored: boolean): void {
  const p = g.getAttribute('position');
  const indices = new Uint16Array(p.count * 4); const weights = new Float32Array(p.count * 4);
  const smooth = (x: number, lo: number, hi: number) => THREE.MathUtils.smoothstep(x, lo, hi);
  // Blend widths follow the reference humanoid's skinning (see villainProportions): about ±0.03
  // of stature at the elbow and ±0.04 at the knee, centred on the joint; the trapezius blends
  // broadly into the clavicle (shoulder joint), the deltoid narrowly into the upper arm.
  for (let i = 0; i < p.count; i++) {
    const x = p.getX(i); const y = p.getY(i); const z = p.getZ(i);
    let a = joint; let b = joint; let c = joint; let t = 0; let tc = 0;
    if (!tailored && joint !== Joint.upperLegL && joint !== Joint.upperLegR) {
      // Other outfits only use the sculpted trousers: everything else stays rigid.
    } else if (joint === Joint.chest) {
      if (y < 0.08) { a = Joint.spine; b = Joint.chest; t = smooth(y, -0.07, 0.08); }
      else {
        const left = x > 0;
        b = left ? Joint.upperArmL : Joint.upperArmR;
        c = left ? Joint.shoulderL : Joint.shoulderR;
        t = 0.6 * smooth(Math.abs(x), 0.15 * d.width, 0.235 * d.width) * smooth(y, 0.12, 0.25);
        tc = (1 - t) * 0.5 * smooth(Math.abs(x), 0.05 * d.width, 0.16 * d.width) * smooth(y, 0.2, 0.31);
      }
    } else if (joint === Joint.spine) {
      b = Joint.chest; t = smooth(y - d.chest, -0.07, 0.08);
    } else if (joint === Joint.upperArmL || joint === Joint.upperArmR) {
      if (y > -0.08) {
        b = joint === Joint.upperArmL ? Joint.shoulderL : Joint.shoulderR;
        t = 0.48 * smooth(y, -0.08, 0.06);
      } else {
        b = joint === Joint.upperArmL ? Joint.lowerArmL : Joint.lowerArmR;
        t = smooth(-y, d.upperArm - 0.055, d.upperArm + 0.055);
      }
    } else if (joint === Joint.upperLegL || joint === Joint.upperLegR) {
      b = joint === Joint.upperLegL ? Joint.lowerLegL : Joint.lowerLegR;
      t = smooth(-y, d.upperLeg - 0.075, d.upperLeg + 0.075);
    } else if (joint === Joint.coatTail) {
      // The hem follows its nearer thigh; the top is held by the waist. The original coatTail
      // sway still contributes, while front panels move far enough to clear a forward stride.
      b = x >= 0 ? Joint.upperLegL : Joint.upperLegR;
      t = smooth(-y, 0.045, 0.27) * (z > 0 ? 0.94 : 0.72);
      if (y > -0.045) { a = Joint.spine; b = Joint.coatTail; t = smooth(-y, 0, 0.045); }
    }
    indices.set([a, b, c, 0], i * 4); weights.set([1 - t - tc, t, tc, 0], i * 4);
  }
  g.setAttribute('skinIndex', new THREE.Uint16BufferAttribute(indices, 4));
  g.setAttribute('skinWeight', new THREE.Float32BufferAttribute(weights, 4));
}

export function villainTail(b: GeoBuilder, d: Proportions, detail: Detail): void {
  const n = detail === 'high' ? 12 : 8;
  const w = d.waistRadius; const dep = d.depth;
  const rings: Ring[] = [
    { y: -0.53, r: w * 1.27, rz: w * 1.06 * dep },
    { y: -0.51, r: w * 1.28, rz: w * 1.07 * dep },
    { y: -0.34, r: w * 1.25, rz: w * 1.05 * dep },
    { y: -0.18, r: w * 1.2, rz: w * 1.04 * dep },
    { y: -0.10, r: w * 1.17, rz: w * 1.02 * dep },
    { y: -0.053, r: w * 1.15, rz: w * 1.0 * dep },
    { y: 0.006, r: w * 1.0, rz: w * 0.81 * dep },
  ].map(r => ({ ...r, y: r.y * d.height, dz: 0.01 }));
  // Separate front opening and back vent. Each half follows its own thigh without triangles
  // stretching between the legs. The inward facing and turned hem make the cloth solid from below.
  for (const side of [-1, 1]) for (const inner of [false, true]) {
    const pos: number[] = []; const idx: number[] = [];
    for (const r of rings) {
      const gap = 0.008 + 0.042 * THREE.MathUtils.smoothstep(-r.y, 0.1, 0.5);
      for (let k = 0; k <= n; k++) {
        const a = gap + (Math.PI - 2 * gap) * k / n;
        const fold = Math.sin(a * 4) * 0.004 * THREE.MathUtils.smoothstep(-r.y, 0.08, 0.45);
        const inset = inner ? 0.005 : 0;
        pos.push(side * Math.sin(a) * (r.r + fold - inset), r.y, Math.cos(a) * (r.rz! + fold - inset) + r.dz!);
      }
    }
    for (let row = 0; row < rings.length - 1; row++) for (let k = 0; k < n; k++) {
      const a = row * (n + 1) + k; const c = a + n + 1;
      if ((side === 1) !== inner) idx.push(a, a + 1, c, a + 1, c + 1, c);
      else idx.push(a, c, a + 1, a + 1, c, c + 1);
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3)); g.setIndex(idx);
    b.add(g, Tone.A, inner ? 0.68 : 0.97, (x, y, z) => (y < -0.51 * d.height ? 0.84 : 1) * (Math.abs(x) < 0.018 && z > 0 ? 0.8 : 1));
  }
}

export function villainHead(sink: Sink, d: Proportions, detail: Detail, style: CharacterStyle): void {
  const r = d.headRadius; const hc = r * 0.85; const n = detail === 'high' ? 20 : 12;
  const b = sink(Joint.head);
  // One continuous jaw, cheeks and forehead. Broad cheek planes taper into a firm chin.
  const rings: Ring[] = [
    { y: -0.91, r: 0.35, rz: 0.43, dz: 0.17 },
    { y: -0.78, r: 0.62, rz: 0.61, dz: 0.1 },
    { y: -0.51, r: 0.78, rz: 0.76, dz: 0.03 },
    { y: -0.17, r: 0.89, rz: 0.86 },
    { y: 0.16, r: 0.86, rz: 0.88 },
    { y: 0.48, r: 0.86, rz: 0.87 },
    { y: 0.76, r: 0.72, rz: 0.73 },
    { y: 0.96, r: 0.39, rz: 0.42 },
  ].map(v => ({ y: hc + v.y * r, r: v.r * r, rz: v.rz * r, dz: (v.dz ?? 0) * r }));
  b.add(lathe(rings, n, { top: true, bottom: true }), Tone.A, 1, (_x, y, z) => (y < hc - r * 0.55 ? 0.86 : 1) * (z < 0 ? 0.93 : 1));
  const face = surface(rings, n);
  const patch = (p: readonly Point[], tone: number, shade: number, lift: number) => panel(b, p.map(([x, y]) => [x * r, hc + y * r]), (x, y) => face(x, y) + lift * r, tone, shade, 0, face);
  sink(Joint.neck).add(lathe([
    { y: -0.015, r: 0.074 * d.width, rz: 0.064 * d.width },
    { y: 0.05, r: 0.06 * d.width, rz: 0.058 * d.width },
    { y: 0.13, r: 0.059 * d.width, rz: 0.057 * d.width },
  ], detail === 'high' ? 12 : 8, { bottom: true }), Tone.A, 0.87);
  for (const s of [-1, 1]) {
    const mirror = (pts: readonly Point[]): Point[] => pts.map(([x, y]) => [x * s, y]);
    // Almond sockets, warm ivory sclera, restrained pupils and a hooded upper lid.
    patch(mirror([[0.12, 0.085], [0.23, 0.15], [0.46, 0.12], [0.59, 0.015], [0.45, -0.08], [0.24, -0.08]]), Tone.A, 0.68, 0.012);
    patch(mirror([[0.19, 0.035], [0.31, 0.071], [0.48, 0.03], [0.43, -0.025], [0.26, -0.03]]), Tone.WHITE, 0.79, 0.027);
    b.add(oval(0.047 * r, 0.053 * r, 0.018 * r, detail).translate(s * r * 0.33, hc + r * 0.012, face(s * r * 0.33, hc) + r * 0.044), Tone.EYE, 1);
    patch(mirror([[0.15, 0.12], [0.27, 0.18], [0.48, 0.15], [0.61, 0.05], [0.45, 0.075], [0.28, 0.095]]), Tone.A, 1.01, 0.043);
    patch(mirror([[0.12, 0.225], [0.3, 0.29], [0.52, 0.25], [0.62, 0.17], [0.39, 0.2], [0.15, 0.16]]), Tone.B, 0.85, 0.038);
    // Ears have a visible rim and inset concha; the sideburn sits in front, not over the ear.
    b.add(oval(r * 0.145, r * 0.245, r * 0.145, detail).rotateZ(-s * 0.13).translate(s * r * 0.87, hc - r * 0.11, -r * 0.03), Tone.A, 0.95);
    if (detail === 'high') b.add(oval(r * 0.055, r * 0.135, r * 0.06).translate(s * r * 0.97, hc - r * 0.11, r * 0.04), Tone.A, 0.7);
    patch(mirror([[0.37, -0.23], [0.61, -0.14], [0.71, -0.27], [0.53, -0.4]]), Tone.A, 1.035, 0.006);
  }
  // Tapered nose bridge, projecting tip and restrained nostril shadows.
  const nose = new THREE.BufferGeometry();
  nose.setAttribute('position', new THREE.Float32BufferAttribute([
    -0.105, 0.19, 0.86, 0.105, 0.19, 0.86, -0.13, -0.24, 1.11, 0.13, -0.24, 1.11,
    0, -0.22, 1.16, -0.15, -0.31, 0.93, 0.15, -0.31, 0.93, 0, -0.34, 1.09,
  ].map(v => v * r), 3));
  nose.setIndex([0, 2, 4, 0, 4, 1, 1, 4, 3, 2, 5, 7, 2, 7, 4, 4, 7, 3, 3, 7, 6, 0, 5, 2, 1, 3, 6]);
  nose.translate(0, hc, 0); b.add(nose, Tone.A, 1.03);
  b.add(oval(r * 0.115, r * 0.083, r * 0.074, detail).translate(0, hc - r * 0.235, r * 1.11), Tone.A, 1.015);
  patch([[-0.28, -0.53], [0, -0.55], [0.28, -0.51], [0.18, -0.58], [-0.17, -0.59]], Tone.C, 0.81, 0.013);
  patch([[-0.23, -0.62], [0.23, -0.62], [0.16, -0.69], [-0.17, -0.7]], Tone.A, 1.04, 0.006);
  const facial = style.facial ?? (style.moustache ? 'mutton' : 'none');
  for (const s of [-1, 1]) {
    if (facial === 'moustache' || facial === 'mutton' || facial === 'beard') {
      patch([[s * 0.025, -0.34], [s * 0.18, -0.35], [s * 0.34, -0.4], [s * 0.39, -0.53], [s * 0.26, -0.51], [s * 0.13, -0.45], [s * 0.02, -0.47]], Tone.B, 0.74, 0.055);
    }
    if (facial === 'mutton' || facial === 'sideburns' || facial === 'beard') {
      patch([[s * 0.79, 0.24], [s * 0.87, 0.11], [s * 0.8, -0.42], [s * 0.62, -0.66], [s * 0.51, -0.59], [s * 0.68, -0.28]], Tone.B, 0.87, 0.016);
    }
  }
  if (facial === 'beard') patch([[-0.63, -0.51], [-0.27, -0.61], [0.27, -0.61], [0.63, -0.51], [0.42, -0.84], [0, -0.9], [-0.42, -0.84]], Tone.B, 0.85, 0.019);
}

export function villainCap(b: GeoBuilder, d: Proportions, detail: Detail): void {
  const r = d.headRadius; const hc = r * 0.85; const n = detail === 'high' ? 24 : 12;
  const crown = lathe([
    { y: 0.49, r: 0.9, rz: 1.02, dz: 0.015 },
    { y: 0.62, r: 1.055, rz: 1.12, dz: 0.08 },
    { y: 0.83, r: 1.035, rz: 1.045, dz: -0.04 },
    { y: 1.04, r: 0.82, rz: 0.87, dz: -0.16 },
    { y: 1.17, r: 0.47, rz: 0.51, dz: -0.2 },
    { y: 1.21, r: 0.06, rz: 0.09, dz: -0.2 },
  ], n, { top: true, bottom: true });
  // Soft forward slope with a slight sideways cant, fuller at the back than over the peak.
  const p = crown.getAttribute('position');
  for (let i = 0; i < p.count; i++) p.setY(i, p.getY(i) - 0.16 * p.getZ(i) + 0.065 * p.getX(i));
  crown.scale(r, r, r).translate(0, hc, 0); crown.computeVertexNormals();
  b.add(crown, Tone.A, 1, (x, y, z) => {
    const a = Math.atan2(x, z + r * 0.1);
    const seam = Math.pow(Math.abs(Math.cos(a * 3)), 28);
    return (0.96 + 0.055 * Math.sin(a * 9 + y * 80)) * (1 - 0.09 * seam);
  });
  const peak = oval(r * 0.83, r * 0.049, r * 0.55, detail).rotateX(0.17).rotateZ(0.065).translate(0, hc + r * 0.42, r * 0.83);
  b.add(peak, Tone.A, 0.73);
  if (detail === 'high') b.add(oval(r * 0.047, r * 0.027, r * 0.049).translate(0, hc + r * 0.45, r * 1.19), Tone.A, 0.86);
}

export function villainHand(b: GeoBuilder, side: number, d: Proportions, detail: Detail): void {
  const h = d.handSize * 1.12;
  b.add(oval(h * 0.36, h * 0.68, h * 0.56, detail).translate(0, -h * 0.64, 0), Tone.A, 1);
  b.add(oval(h * 0.31, h * 0.35, h * 0.36, detail).translate(0, -h * 0.04, 0), Tone.A, 0.94);
  const fingers = detail === 'high' ? [0.4, 0.13, -0.14, -0.39] : [0.25, -0.25];
  for (const z of fingers) {
    const len = 1 - Math.abs(z) * 0.45;
    b.add(oval(h * 0.18, h * 0.39 * len, h * (detail === 'high' ? 0.145 : 0.26), detail).rotateZ(-side * 0.25).translate(-side * h * 0.055, -h * 1.2, z * h), Tone.A, 0.97);
    b.add(oval(h * 0.26, h * 0.17, h * (detail === 'high' ? 0.135 : 0.25), detail).translate(-side * h * 0.22, -h * (1.45 - Math.abs(z) * 0.15), z * h), Tone.A, 0.91);
  }
  b.add(oval(h * 0.23, h * 0.4, h * 0.23, detail).rotateX(-0.55).rotateZ(side * 0.3).translate(-side * h * 0.25, -h * 0.75, h * 0.53), Tone.A, 1.02);
  b.add(oval(h * 0.2, h * 0.25, h * 0.19, detail).rotateX(-0.9).translate(-side * h * 0.35, -h * 0.98, h * 0.69), Tone.A, 0.96);
}

export function villainFoot(b: GeoBuilder, d: Proportions, detail: Detail): void {
  const w = d.width; const h = d.height; const y = -d.ankle;
  // Broad almond toe, a raised instep and a low stacked heel: period leather shoes.
  b.add(lathe([
    { y: y + 0.018, r: 0.058 * w, rz: 0.161 * h, dz: 0.065 * h },
    { y: y + 0.036, r: 0.059 * w, rz: 0.16 * h, dz: 0.065 * h },
    { y: y + 0.069, r: 0.053 * w, rz: 0.146 * h, dz: 0.057 * h },
    { y: y + 0.1, r: 0.043 * w, rz: 0.084 * h, dz: 0.008 },
    { y: y + 0.115, r: 0.037 * w, rz: 0.047 * h, dz: -0.012 },
  ], detail === 'high' ? 16 : 10, { top: true, bottom: true }), Tone.A, 1.14, (_x, yy, z) => z > 0.14 && yy > y + 0.05 ? 1.18 : 1);
  b.add(lathe([{ y: y + 0.004, r: 0.06 * w, rz: 0.163 * h, dz: 0.065 * h }, { y: y + 0.024, r: 0.06 * w, rz: 0.163 * h, dz: 0.065 * h }], detail === 'high' ? 16 : 10, { bottom: true }), Tone.SOLE, 0.93);
  b.add(box(0.098 * w, 0.022, 0.073 * h).translate(0, y + 0.012, -0.052 * h), Tone.SOLE, 0.8);
  if (detail === 'high') for (let i = 0; i < 3; i++) b.add(box(0.041 * w, 0.003, 0.004).translate(0, y + 0.11 - i * 0.004, 0.018 + i * 0.014), Tone.A, 0.65);
}

export function villainSleeve(b: GeoBuilder, d: Proportions, detail: Detail): void {
  const r = 0.044 * d.limb; const y = -d.lowerArm;
  b.add(lathe([{ y: y + 0.01, r: r * 1.06 }, { y: y + 0.025, r: r * 1.08 }, { y: y + 0.048, r: r * 1.04 }], detail === 'high' ? 12 : 8), Tone.A, 0.87);
  b.add(lathe([{ y: y - 0.008, r: r * 0.93 }, { y: y + 0.013, r: r * 0.93 }], detail === 'high' ? 12 : 8), Tone.B, 0.93);
  if (detail === 'high') for (let i = 0; i < 2; i++) b.add(oval(0.005, 0.005, 0.003).translate(0.023, y + 0.028 + i * 0.012, -r * 0.85), Tone.A, 0.42);
}
